/**
 * All redaktionell sidtext på svenska och engelska.
 * Tjänster, case och artiklar hämtas från API:et och är därför inte översatta här.
 */

export const languages = ['sv', 'en'] as const;

export type Language = (typeof languages)[number];

export interface Problem {
  quote: string;
  answer: string;
  service: string;
}

export interface Step {
  num: string;
  title: string;
  text: string;
}

export interface Topic {
  /** Skickas till API:et och är därför identisk på båda språken. */
  value: string;
  label: string;
}

const sv = {
  htmlLang: 'sv',
  a11y: {
    skipToContent: 'Hoppa till innehåll',
    mainMenu: 'Huvudmeny',
    openMenu: 'Öppna meny',
    language: 'Välj språk'
  },
  nav: {
    services: 'Tjänster',
    cases: 'Case',
    insights: 'Insights',
    about: 'Om oss',
    contact: 'Kontakt',
    cta: 'Diskutera er utmaning'
  },
  common: {
    loading: 'Hämtar innehåll…',
    loadError: 'Innehållet kunde inte hämtas just nu. Försök igen om en stund.',
    all: 'Alla',
    readMore: 'Läs mer →',
    readArticle: 'Läs artikeln →',
    comingSoon: 'Publiceras inom kort'
  },
  cta: {
    eyebrow: 'Har ni en utmaning?',
    title: 'Låt oss börja med 30 minuter.',
    button: 'Ta kontakt →'
  },
  serviceBlock: {
    what: 'Vad vi gör',
    how: 'Så arbetar vi',
    outcome: 'Resultat'
  },
  caseCard: {
    named: 'Namngivet case',
    anonymous: 'Anonymiserat case',
    challenge: 'Utmaning',
    role: 'Roll',
    approach: 'Angreppssätt',
    outcome: 'Resultat',
    insight: 'Viktigaste lärdomen'
  },
  home: {
    metaTitle: 'Från komplexitet till framdrift',
    metaDescription:
      'NorthBridge One hjälper organisationer att omsätta komplex transformation till konkret affärsnytta – transformationsledning, business & IT advisory, due diligence och interim ledarskap.',
    eyebrow: 'Strategi · Transformation · Genomförande',
    titleTop: 'Från komplexitet',
    titleEm: 'till framdrift.',
    lead: 'NorthBridge One hjälper organisationer att omsätta komplex transformation till konkret affärsnytta – från strategi och verksamhet till arkitektur, program och genomförande.',
    primaryCta: 'Diskutera er utmaning',
    secondaryCta: 'Se vad vi gör',
    heroMeta: [
      { value: 'Transformation & Advisory', label: 'Strategi, rådgivning och senior förändringsledning' },
      { value: 'Assessment & Readiness', label: 'Förstå nuläget och vad som krävs för nästa steg' },
      { value: 'Transformation & Delivery', label: 'Från strategi till genomförande och resultat' }
    ],
    chain: ['STRATEGI', 'VERKSAMHET', 'IT', 'GENOMFÖRANDE', 'RESULTAT'],
    statement: {
      before: 'Det räcker inte att veta ',
      strong: 'vart',
      after: ' man ska.',
      title: 'Det måste också hända.',
      muted: 'Vi kliver in där riktning, beslut och genomförande behöver mötas.'
    },
    problemsHead: {
      eyebrow: 'Känner ni igen er?',
      title: ['Vi börjar i ert problem –', 'sedan sätter vi rätt kompetens runt det.'],
      text: 'De flesta uppdrag startar i någon av de här situationerna. Om en av dem känns bekant är det förmodligen värt ett samtal.'
    },
    problems: [
      {
        quote: 'Vi har en strategi – men transformationen går för långsamt.',
        answer:
          'Vi går in med senior transformationsledning och skapar struktur, prioritering och framdrift. Strategin blir till styrning, ansvar och leverans.',
        service: 'Transformation Leadership'
      },
      {
        quote: 'Vårt IT-landskap har blivit för komplext.',
        answer:
          'Vi kartlägger nuläget, identifierar de verkliga problemområdena och skapar en tydlig målbild och roadmap som verksamheten kan stå bakom.',
        service: 'Business & IT Advisory'
      },
      {
        quote: 'Vi står inför ett stort teknik- eller systembeslut.',
        answer:
          'Vi bidrar med en oberoende analys och ett beslutsunderlag som gör konsekvenserna tydliga – innan investeringen är gjord.',
        service: 'Business & IT Advisory'
      },
      {
        quote: 'Vi behöver senior kompetens – nu.',
        answer:
          'Vi kliver in som Transformation Lead, Program Director, Program Manager eller interim IT-ledning och tar operativt ansvar från dag ett.',
        service: 'Interim & Senior Advisory'
      },
      {
        quote: 'Vi ska köpa, sälja eller investera i ett bolag.',
        answer:
          'Vi genomför IT- och digital due diligence: systemlandskap, arkitektur, tekniska risker, kostnadsbild och transformationsbehov efter förvärv.',
        service: 'Due Diligence'
      },
      {
        quote: 'Vi vet att något inte fungerar – men inte riktigt vad.',
        answer:
          'En strukturerad Health Check ger en oberoende bild av nuläge, risker och de åtgärder som bör prioriteras först.',
        service: 'System & Digital Health Check'
      }
    ] as Problem[],
    servicesHead: {
      eyebrow: 'Vad vi gör',
      title: ['Fem sätt att skapa', 'verkligt värde.'],
      text: 'Vi grupperar vårt erbjudande kring de problem och möjligheter som skapar störst värde för kunden.'
    },
    allServices: 'Alla tjänsteområden →',
    casesHead: {
      eyebrow: 'Utvalda case',
      title: ['Liknande situationer,', 'tidigare löst.'],
      text: 'Uppdrag publiceras namngivna eller anonymiserade beroende på vad som får kommuniceras.'
    },
    allCases: 'Se alla case →',
    why: {
      eyebrow: 'Varför NorthBridge One',
      titleTop: 'Senior erfarenhet.',
      titleBottom: 'Praktiskt fokus.',
      large:
        'Vi kombinerar affärsförståelse, digital utveckling, IT-arkitektur och förändringsledning för att skapa framdrift i organisationer där komplexiteten har blivit ett hinder.',
      body: 'NorthBridge One är byggt för uppdrag där det krävs både helhetsperspektiv och förmåga att få saker att hända. Vi arbetar nära ledning, verksamhet och teknik – med fokus på beslut, prioriteringar och mätbara resultat.',
      link: 'Läs mer om NorthBridge One →',
      stats: [
        { title: 'Oberoende', text: 'Ingen leverantörs- eller plattformsagenda.' },
        { title: 'Senior kompetens', text: 'Erfarenhet som omsätts i beslut och genomförande.' },
        { title: 'Affärsnära', text: 'Teknik värderas utifrån affärseffekt.' },
        { title: 'Genomförande', text: 'Vi stannar kvar tills det händer.' }
      ]
    },
    processHead: {
      eyebrow: 'Vårt arbetssätt',
      title: ['Riktning först.', 'Sedan genomförande.']
    },
    workSteps: [
      { num: '01', title: 'Förstå', text: 'Vi skapar en gemensam bild av nuläge, mål och de verkliga problemen.' },
      { num: '02', title: 'Prioritera', text: 'Vi skiljer det viktiga från det brådskande och tydliggör besluten.' },
      { num: '03', title: 'Förflytta', text: 'Vi omsätter planen i styrning, aktiviteter, ansvar och konkret leverans.' },
      { num: '04', title: 'Följa upp', text: 'Vi mäter framdrift och justerar när verkligheten förändras.' }
    ] as Step[],
    insightsHead: {
      eyebrow: 'NorthBridge Insights',
      title: ['Perspektiv från', 'verkliga uppdrag.']
    },
    allInsights: 'Alla artiklar →'
  },
  services: {
    metaTitle: 'Tjänster',
    metaDescription:
      'Transformation leadership, business & IT advisory, due diligence, system health check och interim ledarskap – grupperat kring kundvärde.',
    heroEyebrow: 'Tjänster',
    heroTitle: ['Fem sätt att skapa', 'verkligt värde.'],
    heroLead:
      'Vi presenterar inte en lista med konsultroller. Vi grupperar erbjudandet kring de problem som faktiskt behöver lösas – och det värde ni får ut på andra sidan.',
    engagementHead: {
      eyebrow: 'Så börjar ett uppdrag',
      title: ['Ett samtal.', 'Sedan ett tydligt förslag.'],
      text: 'Vi börjar alltid med att förstå situationen innan vi föreslår en insats. Det gör att omfattning, leverans och pris kan vara tydliga från start.'
    },
    engagementSteps: [
      { num: '01', title: 'Första samtalet', text: '30 minuter om situationen, målbilden och vad som är i vägen.' },
      { num: '02', title: 'Avgränsning', text: 'Vi föreslår en insats med tydlig omfattning och förväntad leverans.' },
      { num: '03', title: 'Genomförande', text: 'Vi arbetar nära ledning, verksamhet och teknik.' },
      { num: '04', title: 'Överlämning', text: 'Resultatet ska kunna förvaltas av er organisation.' }
    ] as Step[],
    ctaEyebrow: 'Vilket område är mest aktuellt?'
  },
  cases: {
    metaTitle: 'Case',
    metaDescription:
      'Utvalda uppdrag inom transformation, arkitektur och digitalisering – publicerade namngivet eller anonymiserat.',
    heroEyebrow: 'Case',
    heroTitle: ['Liknande situationer,', 'tidigare löst.'],
    heroLead:
      'Våra case visar inte allt vi har gjort – utan situationer där vår samlade kompetens har skapat konkret resultat. Case publiceras med kundnamn eller anonymiserat beroende på vad som kan kommuniceras.',
    filterLabel: 'Filtrera case',
    empty: 'Inga case matchar det valda filtret.',
    experience: {
      eyebrow: 'Nivå 3',
      titleTop: 'Selected',
      titleBottom: 'experience.',
      large:
        'Vi har stöttat organisationer inom industri, infrastruktur, energi, offentlig sektor och digitala verksamheter.',
      body: 'På grund av sekretess kan vissa uppdrag inte beskrivas publikt. Vi delar gärna mer i ett samtal, inom ramen för vad som är möjligt.',
      link: 'Diskutera vår erfarenhet →'
    },
    ctaEyebrow: 'Står ni inför något liknande?'
  },
  insights: {
    metaTitle: 'NorthBridge Insights',
    metaDescription: 'Analyser och reflektioner om transformation, arkitektur och digitalt ledarskap.',
    heroEyebrow: 'NorthBridge Insights',
    heroTitle: ['Perspektiv från', 'verkliga uppdrag.'],
    heroLead:
      'Analyser och reflektioner om transformation, arkitektur och digitalt ledarskap. Varje artikel kopplas till ett relevant case och det tjänsteområde där perspektivet hör hemma.',
    filterLabel: 'Filtrera artiklar',
    empty: 'Inga artiklar matchar det valda filtret.',
    ctaEyebrow: 'Vill ni diskutera ett av ämnena?'
  },
  insight: {
    metaFallbackTitle: 'NorthBridge Insights',
    metaFallbackDescription: 'Artikel från NorthBridge Insights.',
    loading: 'Hämtar artikeln…',
    notFoundTitle: 'Artikeln kunde inte hittas',
    notFoundText: 'Artikeln finns inte publicerad ännu. Se alla artiklar under NorthBridge Insights.',
    notFoundLink: 'Till Insights',
    readingSuffix: 'läsning',
    nextStep: 'Nästa steg',
    nextStepTitle: 'Känner ni igen situationen?',
    relatedCase: 'Relaterat case:',
    relatedService: 'Relaterad tjänst:',
    cta: 'Diskutera er situation'
  },
  about: {
    metaTitle: 'Om NorthBridge One',
    metaDescription:
      'Senior erfarenhet och praktiskt fokus. NorthBridge One är en boutique-rådgivningsfirma för komplex transformation.',
    heroEyebrow: 'Om NorthBridge One',
    heroTitle: ['Senior erfarenhet.', 'Praktiskt fokus.'],
    heroLead:
      'NorthBridge One hjälper organisationer att navigera komplex transformation inom verksamhet, teknik och organisation. Vi kombinerar affärsförståelse, digital utveckling, IT-arkitektur och transformationsledning – för att hjälpa organisationer att fatta bättre beslut och omsätta strategi till handling.',
    differenceEyebrow: 'Vad som skiljer oss',
    differenceTitleTop: 'En boutique',
    differenceTitleBottom: '- rådgivningsfirma.',
    differenceLead:
      'Vi är inte ytterligare ett traditionellt IT-konsultbolag. Vi tar uppdrag där det behövs senior kompetens som kan se helheten och få saker att hända.',
    principles: [
      {
        title: 'Oberoende',
        text: 'Vi är inte bundna till en viss leverantör, plattform eller teknik. Våra rekommendationer utgår från verksamhetens behov, situationen och det resultat som ska uppnås.'
      },
      {
        title: 'Senior kompetens hela vägen',
        text: 'Senior kompetens finns nära uppdraget från analys och beslut till genomförande.'
      },
      {
        title: 'Affär före teknik',
        text: 'Teknikval värderas utifrån affärseffekt, risk och förvaltningsbarhet – inte utifrån trend.'
      },
      {
        title: 'Genomförande',
        text: 'Ett beslutsunderlag är inte målet. Målet är den förändring som faktiskt blir av.'
      }
    ],
    profileEyebrow: 'Bakom NorthBridge One',
    profileTitle: ['Senior kompetens med erfarenhet'],
    profileRole:
      'Founder · Transformation · Business & IT Strategy · Enterprise Architecture',
    profileFacts: [
      '30+ års erfarenhet',
      'Senior ledarerfarenhet',
      'Transformation & programledning',
      'Digital utveckling',
      'Business & Enterprise Architecture',
      'Strategisk rådgivning'
    ],
    profileLead:
      'NorthBridge One bygger på lång erfarenhet av komplex transformation, verksamhetsutveckling, digital utveckling, IT-arkitektur och strategisk rådgivning.',
    profileBody1:
      'Bolaget grundades av Jan-Olof Eriksson efter mer än 30 år i roller inom bland annat transformation, programledning, enterprise architecture och digital utveckling. Den erfarenheten är en del av den seniora kompetens som NorthBridge One tar med in i sina uppdrag.',
    profileBody2:
      'NorthBridge One är byggt för att kunna samla rätt senior kompetens kring varje uppdrag – beroende på situation, behov och mål.',
    selectedExperience: {
      title: 'Utvald erfarenhet',
      body: 'NorthBridge One har erfarenhet från uppdrag inom industri, infrastruktur, energi, offentlig sektor och digitala verksamheter. Av sekretesskäl kan vissa uppdrag inte beskrivas publikt.'
    },
    selectedExperienceLink: 'Se utvalda case →',
    principleEyebrow: 'Vår princip',
    principleTitleTop: 'Rätt person ska',
    principleTitleBottom: 'känna igen sig.',
    principleLarge: 'Vi försöker inte berätta allt vi kan.',
    principleBody:
      'Den ska få rätt person att tänka: ”Det här är precis den typ av problem vi har.” Sedan: ”De verkar ha gjort det här tidigare.” Och slutligen: ”Jag borde prata med dem.”',
    principleLink: 'Starta ett samtal →'
  },
  contact: {
    metaTitle: 'Kontakt',
    metaDescription: 'Berätta vad ni försöker lösa. Första samtalet tar 30 minuter och kostar ingenting.',
    heroEyebrow: 'Kontakt',
    heroTitle: ['Berätta vad ni', 'försöker lösa.'],
    heroLead:
      'Oavsett om ni behöver en interim ledare, en transformationspartner eller ett oberoende beslutsunderlag börjar vi med att förstå situationen. Första samtalet tar 30 minuter och kostar ingenting.',
    directTitle: 'Direktkontakt',
    emailLabel: 'E-post',
    outcomeTitle: 'Vad händer efter att ni hört av er?',
    outcomeText:
      'Vi återkommer inom en arbetsdag och föreslår ett kort samtal. Efter samtalet får ni ett förslag med tydlig omfattning, leverans och pris – eller ett ärligt besked om att någon annan passar bättre.',
    nameLabel: 'Namn',
    companyLabel: 'Företag',
    roleLabel: 'Roll',
    phoneLabel: 'Telefon',
    phoneHint: '(valfritt)',
    topicLabel: 'Vad vill du diskutera?',
    messageLabel: 'Kort om situationen',
    messagePlaceholder: 'Vad står ni inför just nu?',
    honeypotLabel: 'Lämna tomt',
    submit: 'Skicka förfrågan',
    submitting: 'Skickar…',
    formNote: 'Uppgifterna används endast för att besvara din förfrågan.',
    sent: 'Tack! Vi återkommer inom en arbetsdag.',
    failed: 'Förfrågan kunde inte skickas just nu. Försök igen, eller mejla oss direkt.',
    requiredError: 'Fältet behöver fyllas i.',
    emailError: 'Ange en giltig e-postadress.',
    topics: [
      { value: 'Transformation', label: 'Transformation' },
      { value: 'IT Strategy', label: 'IT Strategy' },
      { value: 'Architecture', label: 'Architecture' },
      { value: 'Interim Leadership', label: 'Interim Leadership' },
      { value: 'Due Diligence', label: 'Due Diligence' },
      { value: 'System Health Check', label: 'System Health Check' },
      { value: 'Advisory', label: 'Advisory' },
      { value: 'Annat', label: 'Annat' }
    ] as Topic[]
  },
  notFound: {
    metaTitle: 'Sidan kunde inte hittas',
    metaDescription: 'Sidan finns inte längre eller har flyttat.',
    title: 'Sidan kunde inte hittas.',
    lead: 'Länken kan vara gammal eller felstavad. Börja om från startsidan, eller hör av er direkt.',
    home: 'Till startsidan',
    contact: 'Kontakta oss'
  },
  footer: {
    intro: 'Vi hjälper organisationer att omsätta komplex transformation till konkret affärsnytta.',
    pages: 'Sidor',
    contact: 'Kontakt',
    tagline: 'Strategy · Transformation · Advisory'
  },
  /** Filtervärden matchas mot API-data och är därför identiska på båda språken. */
  filters: {
    cases: ['Transformation', 'Architecture', 'Digital', 'Strategy', 'Advisory', 'Leadership'],
    insights: ['Transformation', 'Architecture', 'Strategy', 'Digital', 'Leadership']
  }
};

export type Dictionary = typeof sv;

const en: Dictionary = {
  htmlLang: 'en',
  a11y: {
    skipToContent: 'Skip to content',
    mainMenu: 'Main menu',
    openMenu: 'Open menu',
    language: 'Select language'
  },
  nav: {
    services: 'Services',
    cases: 'Cases',
    insights: 'Insights',
    about: 'About',
    contact: 'Contact',
    cta: 'Discuss your challenge'
  },
  common: {
    loading: 'Loading content…',
    loadError: 'The content could not be loaded right now. Please try again shortly.',
    all: 'All',
    readMore: 'Read more →',
    readArticle: 'Read the article →',
    comingSoon: 'Publishing shortly'
  },
  cta: {
    eyebrow: 'Facing a challenge?',
    title: 'Let us start with 30 minutes.',
    button: 'Get in touch →'
  },
  serviceBlock: {
    what: 'What we do',
    how: 'How we work',
    outcome: 'Outcome'
  },
  caseCard: {
    named: 'Named case',
    anonymous: 'Anonymised case',
    challenge: 'Challenge',
    role: 'Role',
    approach: 'Approach',
    outcome: 'Outcome',
    insight: 'Key takeaway'
  },
  home: {
    metaTitle: 'From complexity to progress.',
    metaDescription:
      'NorthBridge One helps organisations turn complex transformation into tangible business value – transformation leadership, business & IT advisory, due diligence and interim leadership.',
    eyebrow: 'Strategy · Transformation · Execution',
    titleTop: 'From complexity',
    titleEm: 'to progress.',
    lead: 'NorthBridge One helps organisations turn complex transformation into tangible business value – from strategy and operations to architecture, programmes and execution.',
    primaryCta: 'Discuss your challenge',
    secondaryCta: 'See what we do',
    heroMeta: [
      { value: 'Transformation & Advisory', label: 'Strategy, advisory and senior transformation leadership' },
      { value: 'Assessment & Readiness', label: 'Understand where you are and what comes next' },
      { value: 'Transformation & Delivery', label: 'From strategy to execution and measurable results' }
    ],
    chain: ['STRATEGY', 'BUSINESS', 'IT', 'EXECUTION', 'RESULTS'],
    statement: {
      before: 'Knowing ',
      strong: 'where',
      after: ' to go is not enough.',
      title: 'It also has to happen.',
      muted: 'We step in where direction, decisions and execution need to meet.'
    },
    problemsHead: {
      eyebrow: 'Sound familiar?',
      title: ['We start with your problem –', 'then bring the right expertise around it.'],
      text: 'Most engagements begin in one of these situations. If one of them feels familiar, it is probably worth a conversation.'
    },
    problems: [
      {
        quote: 'We have a strategy – but the transformation is moving too slowly.',
        answer:
          'We bring in senior transformation leadership and create structure, prioritisation and momentum. Strategy becomes governance, accountability and delivery.',
        service: 'Transformation Leadership'
      },
      {
        quote: 'Our IT landscape has become too complex.',
        answer:
          'We map the current state, identify the real problem areas and create a clear target state and roadmap that the business can stand behind.',
        service: 'Business & IT Advisory'
      },
      {
        quote: 'We are facing a major technology or system decision.',
        answer:
          'We contribute an independent analysis and a decision base that makes the consequences clear – before the investment is made.',
        service: 'Business & IT Advisory'
      },
      {
        quote: 'We need senior capability – now.',
        answer:
          'We step in as Transformation Lead, Program Director, Program Manager or interim IT leadership and take operational responsibility from day one.',
        service: 'Interim & Senior Advisory'
      },
      {
        quote: 'We are about to buy, sell or invest in a company.',
        answer:
          'We carry out IT and digital due diligence: system landscape, architecture, technical risk, cost profile and post-acquisition transformation needs.',
        service: 'Due Diligence'
      },
      {
        quote: 'We know something is not working – but not quite what.',
        answer:
          'A structured Health Check gives an independent view of the current state, the risks and the actions that should be prioritised first.',
        service: 'System & Digital Health Check'
      }
    ],
    servicesHead: {
      eyebrow: 'What we do',
      title: ['Five ways to create', 'real value.'],
      text: 'We group our offering around the problems and opportunities that create the greatest value for our clients.'
    },
    allServices: 'All service areas →',
    casesHead: {
      eyebrow: 'Selected cases',
      title: ['Similar situations,', 'solved before.'],
      text: 'Cases are published named or anonymised depending on what may be communicated.'
    },
    allCases: 'See all cases →',
    why: {
      eyebrow: 'Why NorthBridge One',
      titleTop: 'Senior experience.',
      titleBottom: 'Practical focus.',
      large:
        'We combine business understanding, digital development, IT architecture and transformation leadership to create momentum in organisations where complexity has become a barrier. ',
      body: 'NorthBridge One is built for engagements that require both a holistic perspective and the ability to turn decisions into action. We work closely with leadership, business and technology – focusing on decisions, priorities and measurable outcomes.',
      link: 'Read more about NorthBridge One →',
      stats: [
        { title: 'Independent', text: 'No vendor or platform agenda.' },
        { title: 'Senior capability', text: 'Experience translated into decisions and execution.' },
        { title: 'Business-led', text: 'Technology and transformation evaluated through business impact.' },
        { title: 'Execution', text: ' We stay involved until decisions become real change.' }
      ]
    },
    processHead: {
      eyebrow: 'How we work',
      title: ['Direction first.', 'Then execution.']
    },
    workSteps: [
      { num: '01', title: 'Understand', text: 'We create a shared view of the current state, the goals and the real problems.' },
      { num: '02', title: 'Prioritise', text: 'We separate the important from the urgent and make the decisions explicit.' },
      { num: '03', title: 'Move', text: 'We turn the plan into governance, activities, accountability and concrete delivery.' },
      { num: '04', title: 'Follow up', text: 'We measure progress and adjust when reality changes.' }
    ],
    insightsHead: {
      eyebrow: 'NorthBridge Insights',
      title: ['Perspectives from', 'real engagements.']
    },
    allInsights: 'All articles →'
  },
  services: {
    metaTitle: 'Services',
    metaDescription:
      'Transformation leadership, business & IT advisory, due diligence, system health check and interim leadership – grouped around client value.',
    heroEyebrow: 'Services',
    heroTitle: ['Five ways to create', 'real value.'],
    heroLead:
      'We do not present a list of consulting roles. We group our offering around the problems that actually need solving – and the value you get out on the other side.',
    engagementHead: {
      eyebrow: 'How an engagement starts',
      title: ['One conversation.', 'Then a clear proposal.'],
      text: 'We always start by understanding the situation before we propose an engagement. That way scope, delivery and price can be clear from the start.'
    },
    engagementSteps: [
      { num: '01', title: 'First conversation', text: '30 minutes about the situation, the target state and what is in the way.' },
      { num: '02', title: 'Scoping', text: 'We propose an engagement with clear scope and expected delivery.' },
      { num: '03', title: 'Delivery', text: 'We work closely with leadership, business and technology.' },
      { num: '04', title: 'Handover', text: 'The result must be manageable by your own organisation.' }
    ],
    ctaEyebrow: 'Which area is most pressing?'
  },
  cases: {
    metaTitle: 'Cases',
    metaDescription:
      'Selected engagements in transformation, architecture and digitalisation – published named or anonymised.',
    heroEyebrow: 'Cases',
    heroTitle: ['Similar situations,', 'solved before.'],
    heroLead:
      'Our cases are not intended to describe everything we have done, but to show situations where our combined expertise has created tangible results. Cases are published with client names or anonymised depending on what can be communicated.',
    filterLabel: 'Filter cases',
    empty: 'No cases match the selected filter.',
    experience: {
      eyebrow: 'Level 3',
      titleTop: 'Selected',
      titleBottom: 'experience.',
      large:
        'We have supported organisations across industry, infrastructure, energy, the public sector and digital businesses.',
      body: 'Due to confidentiality, some engagements cannot be described publicly. We are happy to share more in a conversation, within what is possible.',
      link: 'Discuss our experience →'
    },
    ctaEyebrow: 'Are you facing something similar?'
  },
  insights: {
    metaTitle: 'NorthBridge Insights',
    metaDescription: 'Analysis and reflections on transformation, architecture and digital leadership.',
    heroEyebrow: 'NorthBridge Insights',
    heroTitle: ['Perspectives from', 'real engagements.'],
    heroLead:
      'Analysis and reflections on transformation, architecture and digital leadership. Each article is linked to a relevant case and the service area where the perspective applies.',
    filterLabel: 'Filter articles',
    empty: 'No articles match the selected filter.',
    ctaEyebrow: 'Would you like to discuss one of these topics?'
  },
  insight: {
    metaFallbackTitle: 'NorthBridge Insights',
    metaFallbackDescription: 'Article from NorthBridge Insights.',
    loading: 'Loading the article…',
    notFoundTitle: 'The article could not be found',
    notFoundText: 'The article has not been published yet. See all articles under NorthBridge Insights.',
    notFoundLink: 'Go to Insights',
    readingSuffix: 'read',
    nextStep: 'Next step',
    nextStepTitle: 'Does this situation sound familiar?',
    relatedCase: 'Related case:',
    relatedService: 'Related service:',
    cta: 'Discuss your situation'
  },
  about: {
    metaTitle: 'About NorthBridge One',
    metaDescription:
      'Senior experience and practical focus. NorthBridge One is a boutique advisory firm for complex transformation.',
    heroEyebrow: 'About NorthBridge One',
    heroTitle: ['Senior experience.', 'Practical focus.'],
    heroLead:
      'NorthBridge One helps organisations navigate complex transformation across business, technology and organisation. We combine business understanding, digital development, IT architecture and transformation leadership – to help organisations make better decisions and turn strategy into action.',
    differenceEyebrow: 'What sets us apart',
    differenceTitleTop: 'A boutique',
    differenceTitleBottom: 'advisory firm.',
    differenceLead:
      'We are not yet another traditional IT consultancy. We take on engagements that need senior capability that can both see the whole picture and make things happen.',
    principles: [
      {
        title: 'Independent',
        text: 'We are not tied to a specific vendor, platform or technology. Our recommendations are driven by the business need, the situation and the outcome we need to achieve. '
      },
      {
        title: 'Senior capability throughout',
        text: 'Senior capability stays close to the engagement from analysis and decision-making through to execution. '
      },
      {
        title: 'Business before technology',
        text: 'Technology choices are judged by business impact, risk and maintainability – not by trend.'
      },
      {
        title: 'Execution',
        text: 'A decision base is not the goal. The goal is the change that actually happens.'
      }
    ],
    profileEyebrow: 'Behind NorthBridge One',
    profileTitle: ['Senior expertise. Practical focus.'],
    profileRole:
      'Founder · Transformation · Business & IT Strategy · Enterprise Architecture',
    profileFacts: [
      '30+ years of experience',
      'Senior leadership experience',
      'Transformation & programme management',
      'Digital development',
      'Business & Enterprise Architecture',
      'Strategic advisory'
    ],
    profileLead:
      'NorthBridge One is built on extensive experience in complex transformation, business development, digital development, IT architecture and strategic advisory.',
    profileBody1:
      'The company was founded by Jan-Olof Eriksson after more than 30 years in roles spanning transformation, programme leadership, enterprise architecture and digital development. This experience is part of the senior expertise NorthBridge One brings to its engagements.',
    profileBody2:
      'NorthBridge One is built to bring together the right senior expertise for each engagement – depending on the situation, needs and objectives. ',
    selectedExperience: {
      title: 'Selected experience',
      body: 'NorthBridge One has experience from engagements across industry, infrastructure, energy, the public sector and digital businesses. Due to confidentiality, some engagements cannot be described publicly.'
    },
    selectedExperienceLink: 'See selected cases →',
    principleEyebrow: 'Our principle',
    principleTitleTop: 'The right person',
    principleTitleBottom: 'should recognise themselves.',
    principleLarge: 'We are not trying to tell you everything we can do.',
    principleBody:
      'It should make the right person think: “This is exactly the kind of problem we have.” Then: “They seem to have done this before.” And finally: “I should talk to them.”',
    principleLink: 'Start a conversation →'
  },
  contact: {
    metaTitle: 'Contact',
    metaDescription: 'Tell us what you are trying to solve. The first conversation takes 30 minutes and costs nothing.',
    heroEyebrow: 'Contact',
    heroTitle: ['Tell us what you', 'are trying to solve.'],
    heroLead:
      'Whether you need an interim leader, a transformation partner or an independent decision base, we start by understanding the situation. The first conversation takes 30 minutes and costs nothing.',
    directTitle: 'Direct contact',
    emailLabel: 'Email',
    outcomeTitle: 'What happens after you get in touch?',
    outcomeText:
      'We respond within one working day and propose a short conversation. After the call you receive a proposal with clear scope, delivery and price – or an honest answer that someone else is a better fit.',
    nameLabel: 'Name',
    companyLabel: 'Company',
    roleLabel: 'Role',
    phoneLabel: 'Phone',
    phoneHint: '(optional)',
    topicLabel: 'What would you like to discuss?',
    messageLabel: 'Briefly about the situation',
    messagePlaceholder: 'What are you facing right now?',
    honeypotLabel: 'Leave empty',
    submit: 'Send enquiry',
    submitting: 'Sending…',
    formNote: 'Your details are only used to answer your enquiry.',
    sent: 'Thank you! We will get back to you within one working day.',
    failed: 'The enquiry could not be sent right now. Please try again, or email us directly.',
    requiredError: 'This field is required.',
    emailError: 'Enter a valid email address.',
    topics: [
      { value: 'Transformation', label: 'Transformation' },
      { value: 'IT Strategy', label: 'IT Strategy' },
      { value: 'Architecture', label: 'Architecture' },
      { value: 'Interim Leadership', label: 'Interim Leadership' },
      { value: 'Due Diligence', label: 'Due Diligence' },
      { value: 'System Health Check', label: 'System Health Check' },
      { value: 'Advisory', label: 'Advisory' },
      { value: 'Annat', label: 'Other' }
    ]
  },
  notFound: {
    metaTitle: 'Page not found',
    metaDescription: 'The page no longer exists or has moved.',
    title: 'The page could not be found.',
    lead: 'The link may be old or misspelled. Start again from the home page, or get in touch directly.',
    home: 'Go to the home page',
    contact: 'Contact us'
  },
  footer: {
    intro: 'We help organisations turn complex transformation into tangible business value.',
    pages: 'Pages',
    contact: 'Contact',
    tagline: 'Strategy · Transformation · Advisory'
  },
  filters: {
    cases: ['Transformation', 'Architecture', 'Digital', 'Strategy', 'Advisory', 'Leadership'],
    insights: ['Transformation', 'Architecture', 'Strategy', 'Digital', 'Leadership']
  }
};

export const dictionaries: Record<Language, Dictionary> = { sv, en };
